import type { Theme } from '@mui/material';
import { Box, TextField, Typography } from '@mui/material';
import { type ComponentProps, useCallback } from 'react';
import { combineSx } from '@/utils/combineSx';
import { CARD_BORDER_RADIUS } from '../../../theme/cardStyles';
import { Label } from '../../Label';

const minMaxFieldFilterStyles = {
  container: {
    display: 'grid',
    gap: '0.375rem',
    alignContent: 'start',
  },
  fieldsContainer: (theme: Theme) => ({
    display: 'grid',
    gridTemplateColumns: '1fr auto 1fr',
    alignItems: 'center',
    columnGap: '0.375rem',
    rowGap: '0.25rem',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
      rowGap: '0.375rem',
      '& > span': {
        display: 'none',
      },
    },
  }),
  textField: (theme: Theme) => ({
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.baseColors.white.c100,
      borderRadius: CARD_BORDER_RADIUS,
      transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
      '& fieldset': {
        borderColor: theme.palette.baseColors.iceBlue.c75,
      },
      '&:hover fieldset': {
        borderColor: theme.palette.baseColors.deepSeaNavy.c50,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.baseColors.deepSeaNavy.c100,
      },
    },
    '& input': {
      padding: '0.375rem 0.625rem',
      fontSize: theme.fontSizes.smallest,
      fontWeight: 600,
      letterSpacing: '0.015em',
      color: theme.palette.text.primary,
    },
    '& .MuiInputLabel-root': {
      fontSize: theme.fontSizes.smallest,
      fontWeight: 500,
      letterSpacing: '0.025em',
      color: theme.palette.text.secondary,
      textTransform: 'uppercase',
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: theme.palette.baseColors.deepSeaNavy.c100,
    },
  }),
  separator: (theme: Theme) => ({
    color: theme.palette.text.secondary,
    fontWeight: 600,
    fontSize: theme.fontSizes.smallest,
    opacity: 0.55,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  }),
};

type MinMaxFieldFilterProps = Omit<ComponentProps<typeof Box>, 'onChange'> & {
  minValue: string;
  maxValue: string;
  onChange: (min: string, max: string) => void;
  label: string;
  minLabel?: string;
  maxLabel?: string;
  dataId?: string;
};

const MinMaxFieldFilter = ({
  minValue,
  maxValue,
  onChange,
  label,
  minLabel = 'Min',
  maxLabel = 'Max',
  dataId,
  sx,
}: MinMaxFieldFilterProps) => {
  const handleMinChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      onChange(event.target.value, maxValue);
    },
    [onChange, maxValue],
  );

  const handleMaxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      onChange(minValue, event.target.value);
    },
    [onChange, minValue],
  );

  return (
    <Box
      sx={combineSx(minMaxFieldFilterStyles.container, sx)}
      data-id={dataId ?? 'min-max-field-filter'}
    >
      <Label label={label} />
      <Box sx={minMaxFieldFilterStyles.fieldsContainer}>
        <TextField
          label={minLabel}
          value={minValue}
          onChange={handleMinChange}
          type='number'
          size='small'
          fullWidth
          data-id={`${dataId ?? 'min-max-field'}-min-input`}
          sx={minMaxFieldFilterStyles.textField}
        />
        <Typography component='span' variant='body2' sx={minMaxFieldFilterStyles.separator}>
          to
        </Typography>
        <TextField
          label={maxLabel}
          value={maxValue}
          onChange={handleMaxChange}
          type='number'
          size='small'
          fullWidth
          data-id={`${dataId ?? 'min-max-field'}-max-input`}
          sx={minMaxFieldFilterStyles.textField}
        />
      </Box>
    </Box>
  );
};

export { MinMaxFieldFilter };
